import { GridSortDirection, GridSortItem, GridSortModel } from '@mui/x-data-grid-premium';

import { MAX_SORT_COLUMNS } from '@/shared/lib/sorting';

export type SortDirection = 'asc' | 'desc';

export interface CycleSortLevelOptions {
  /** Append the column as an extra sort level instead of replacing the model. */
  append?: boolean;
}

const nextDirection = (current: GridSortDirection): SortDirection | null =>
  current === 'asc' ? 'desc' : current === 'desc' ? null : 'asc';

export const capSortModel = (model: GridSortModel | undefined): GridSortModel =>
  (model ?? []).slice(0, MAX_SORT_COLUMNS);

/** Returns the original model when the field is not sorted, so callers can skip no-op updates. */
export const removeSortLevel = (model: GridSortModel, field: string): GridSortModel =>
  model.some((item) => item.field === field) ? model.filter((item) => item.field !== field) : model;

export const cycleSortLevel = (
  model: GridSortModel,
  field: string,
  { append = false }: CycleSortLevelOptions = {},
): GridSortModel => {
  const existing = model.find((item) => item.field === field);
  const direction = nextDirection(existing?.sort);

  if (!append) {
    return direction ? [{ field, sort: direction }] : [];
  }

  if (!existing) {
    return model.length >= MAX_SORT_COLUMNS ? model : [...model, { field, sort: 'asc' }];
  }

  if (!direction) {
    return removeSortLevel(model, field);
  }

  return model.map((item) => (item.field === field ? { field, sort: direction } : item));
};

export const setSortLevelDirection = (
  model: GridSortModel,
  field: string,
  direction: SortDirection,
): GridSortModel => {
  const next: GridSortItem = { field, sort: direction };

  if (!model.some((item) => item.field === field)) {
    return model.length >= MAX_SORT_COLUMNS ? model : [...model, next];
  }

  return model.map((item) => (item.field === field ? next : item));
};

export const moveSortLevel = (
  model: GridSortModel,
  field: string,
  delta: number,
): GridSortModel => {
  const from = model.findIndex((item) => item.field === field);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= model.length) return model;

  const next = [...model];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

/**
 * One-based sort position, mirroring the DataGrid's own `sortIndex`: a badge is
 * only meaningful once more than one column is sorted.
 */
export const getSortPriority = (model: GridSortModel, field: string): number | undefined => {
  if (model.length <= 1) return undefined;
  const index = model.findIndex((item) => item.field === field);
  return index === -1 ? undefined : index + 1;
};

/**
 * True when `next` is `current` with only the levels of `unrenderedFields` removed — the one edit
 * the grid makes on its own: MUI unsorts any column that leaves its column set (columnsChange
 * pruning). The timeline rail hands the grid only its `timeline` columns, so a page sort on any
 * other column would otherwise be reported back as removed the moment the rail renders.
 *
 * Sound only while every grid-originated edit touches a RENDERED column's level (a header click
 * cycles its direction, the column menu offers only the directions not yet applied, Unsort only
 * while sorted): re-emitting a rendered level unchanged alongside a drop would look like a prune.
 */
export const dropsOnlyUnrenderedLevels = (
  current: GridSortModel,
  next: GridSortModel,
  unrenderedFields: ReadonlySet<string>,
): boolean => {
  if (unrenderedFields.size === 0 || next.length >= current.length) return false;
  const kept = current.filter((item) => !unrenderedFields.has(item.field));
  return (
    kept.length === next.length &&
    kept.every((item, index) => item.field === next[index].field && item.sort === next[index].sort)
  );
};
